/**
 * KATALOG — jedyne źródło prawdy o tym, jakie wzory wydruków zna Universe.
 *
 * Wszystko inne jest z niego wyprowadzane:
 *   • print-core renderuje po slugu (endpoint `/api/print/render`, Faza 2),
 *   • panel /admin/printing wyświetla galerię i podgląd (`templateCatalog()`),
 *   • `pnpm print:check` porównuje katalog z tabelą w dokumentacji,
 *   • test niezmienników pilnuje kontraktu przy każdym commicie.
 *
 * Dodanie wzoru = wpis tutaj (albo prościej: `pnpm print:new <slug>`).
 * Nie ma drugiej drogi — DB (`print_templates`) przechowuje wyłącznie
 * ODSTĘPSTWA tenanta (decyzja D4); domyślne wzory nigdy nie żyją w bazie,
 * bo siedem miejsc treści maili i edytor PackFlow zapisujący do DB, której
 * silnik druku nie czytał, to dokładnie ta sama wada w dwóch systemach.
 */

import { UnknownPrintTemplateError } from './errors';
import { lexshiftTemplates } from './templates/lexshift';
import { productionTemplates } from './templates/production';
import { systemTemplates } from './templates/system';
import type { PrintTemplateDef, PrintVariable, PrintVars } from './types';

/** Kolejność rodzin = kolejność w panelu: najpierw to, co drukują hale. */
export const ALL_PRINT_TEMPLATES: PrintTemplateDef[] = [
  ...productionTemplates,
  ...lexshiftTemplates,
  ...systemTemplates,
];

const BY_SLUG = new Map<string, PrintTemplateDef>();
for (const t of ALL_PRINT_TEMPLATES) {
  if (BY_SLUG.has(t.slug)) {
    // Duplikat slugu znaczy, że dwa zlecenia trafiają w ten sam wzór i jedno
    // z nich cicho drukuje cudzą etykietę. Lepiej wywalić się przy imporcie.
    throw new Error(`Zduplikowany slug wzoru wydruku: ${t.slug}`);
  }
  BY_SLUG.set(t.slug, t);
}

/**
 * Wzór po slugu — `null`, gdy nie istnieje. Render (`render.ts`) rzuca
 * `UnknownPrintTemplateError`; ta funkcja jest dla wołających, którzy chcą
 * sprawdzić bez łapania wyjątku (panel, print-check).
 */
export function getTemplateDef(slug: string): PrintTemplateDef | null {
  return BY_SLUG.get(slug) ?? null;
}

/** Jak `getTemplateDef`, ale rzuca typowanym błędem — dla ścieżki renderu. */
export function requireTemplateDef(slug: string): PrintTemplateDef {
  const def = BY_SLUG.get(slug);
  if (!def) throw new UnknownPrintTemplateError(slug);
  return def;
}

export function hasTemplateDef(slug: string): boolean {
  return BY_SLUG.has(slug);
}

export function templateSlugs(): string[] {
  return [...BY_SLUG.keys()].sort();
}

/** Zmienne podglądowe wprost z deklaracji — panel i testy (kalka z poczty). */
export function previewVars(def: PrintTemplateDef): PrintVars {
  const out: PrintVars = {};
  for (const v of def.variables) out[v.name] = v.example;
  return out;
}

/** Lekki opis katalogu — do API i panelu, bez ciężaru funkcji `build`. */
export interface PrintTemplateSummary {
  slug: string;
  name: string;
  family: PrintTemplateDef['family'];
  organ: string;
  trigger: string;
  media: PrintTemplateDef['media'];
  targets: PrintTemplateDef['targets'];
  variables: PrintVariable[];
  version: number;
  sladKeys: string[];
}

export function templateCatalog(): PrintTemplateSummary[] {
  return ALL_PRINT_TEMPLATES.map((t) => ({
    slug: t.slug,
    name: t.name,
    family: t.family,
    organ: t.organ,
    trigger: t.trigger,
    media: t.media,
    targets: t.targets,
    variables: t.variables,
    version: t.version,
    sladKeys: t.slad?.keys ?? [],
  }));
}
